import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp, Package, Calendar, Users, Building2, BadgePercent, Search, ShoppingBag, Eye, Clock, CheckCircle2, AlertCircle, AlertTriangle, User } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs as Tabs2, TabsList as TabsList2, TabsTrigger as TabsTrigger2, TabsContent as TabsContent2 } from "@/components/ui/tabs";

export default function Reports() {
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Reportes</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Reportes avanzados</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Márgenes, rotación y bitácoras con exportación PDF/Excel.</p>
      </div>
      <Tabs defaultValue="margen-prod">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="margen-prod"><TrendingUp size={14} className="mr-2" />Margen productos</TabsTrigger>
          <TabsTrigger value="margen-vend"><Users size={14} className="mr-2" />Margen vendedores</TabsTrigger>
          <TabsTrigger value="rotacion"><Package size={14} className="mr-2" />Rotación</TabsTrigger>
          <TabsTrigger value="bitacora"><Calendar size={14} className="mr-2" />Bitácora</TabsTrigger>
          <TabsTrigger value="ventas-cliente" data-testid="tab-ventas-cliente"><Building2 size={14} className="mr-2" />Ventas por cliente</TabsTrigger>
          <TabsTrigger value="comisiones-cobros" data-testid="tab-comisiones-cobros"><BadgePercent size={14} className="mr-2" />Comisiones (cobros)</TabsTrigger>
        </TabsList>
        <TabsContent value="margen-prod"><MargenProductos /></TabsContent>
        <TabsContent value="margen-vend"><MargenVendedores /></TabsContent>
        <TabsContent value="rotacion"><Rotacion /></TabsContent>
        <TabsContent value="bitacora"><Bitacora /></TabsContent>
        <TabsContent value="ventas-cliente"><VentasPorCliente /></TabsContent>
        <TabsContent value="comisiones-cobros"><ComisionesCobros /></TabsContent>
      </Tabs>
    </div>
  );
}

function MargenProductos() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/reports/margen-productos").then(r => setData(r.data)).catch(e => toast.error(formatApiError(e))); }, []);
  const cols = [
    { label: "Código", accessor: "codigo_interno" },
    { label: "Producto", accessor: "nombre_comercial" },
    { label: "Costo", accessor: (r) => `$${r.precio_costo.toFixed(4)}` },
    { label: "Venta", accessor: (r) => `$${r.precio_venta.toFixed(2)}` },
    { label: "Margen $", accessor: (r) => `$${r.margen_abs.toFixed(2)}` },
    { label: "Margen %", accessor: (r) => `${r.margen_pct.toFixed(1)}%` },
  ];
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><ExportButton title="Margen por producto" columns={cols} rows={data} filename="margen-productos" /></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Código</th><th>Producto</th><th className="text-right">Costo</th><th className="text-right">Venta</th><th className="text-right">Margen $</th><th className="text-right">Margen %</th></tr></thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={d.codigo_interno || `mp-${i}`}>
                <td className="font-mono text-xs">{d.codigo_interno}</td>
                <td className="font-medium">{d.nombre_comercial}</td>
                <td className="text-right tabular-nums">${d.precio_costo.toFixed(4)}</td>
                <td className="text-right tabular-nums">${d.precio_venta.toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${d.margen_abs.toFixed(2)}</td>
                <td className="text-right tabular-nums"><Badge className={d.margen_pct >= 30 ? "fefo-safe border" : d.margen_pct >= 15 ? "fefo-critico border" : "fefo-vencido border"}>{d.margen_pct.toFixed(1)}%</Badge></td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">Sin datos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MargenVendedores() {
  const [data, setData] = useState([]);
  const [dias, setDias] = useState(30);
  useEffect(() => { api.get(`/reports/margen-vendedores?dias=${dias}`).then(r => setData(r.data)); }, [dias]);
  const cols = [
    { label: "Vendedor", accessor: "vendedor_nombre" },
    { label: "Pedidos", accessor: "pedidos" },
    { label: "Ventas", accessor: (r) => `$${r.ventas.toFixed(2)}` },
    { label: "Costo", accessor: (r) => `$${r.costo.toFixed(2)}` },
    { label: "Margen $", accessor: (r) => `$${r.margen_abs.toFixed(2)}` },
    { label: "Margen %", accessor: (r) => `${r.margen_pct.toFixed(1)}%` },
  ];
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between gap-2">
        <Select value={String(dias)} onValueChange={(v) => setDias(parseInt(v))}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
            <SelectItem value="180">Últimos 180 días</SelectItem>
          </SelectContent>
        </Select>
        <ExportButton title={`Margen por vendedor (${dias}d)`} columns={cols} rows={data} filename="margen-vendedores" />
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Vendedor</th><th className="text-right">Pedidos</th><th className="text-right">Ventas</th><th className="text-right">Costo</th><th className="text-right">Margen $</th><th className="text-right">Margen %</th></tr></thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={d.vendedor_id || d.vendedor_nombre || `mv-${i}`}>
                <td className="font-medium">{d.vendedor_nombre}</td>
                <td className="text-right tabular-nums">{d.pedidos}</td>
                <td className="text-right tabular-nums">${d.ventas.toFixed(2)}</td>
                <td className="text-right tabular-nums">${d.costo.toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${d.margen_abs.toFixed(2)}</td>
                <td className="text-right tabular-nums">{d.margen_pct.toFixed(1)}%</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">Sin datos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Rotacion() {
  const [data, setData] = useState([]);
  const [dias, setDias] = useState(90);
  useEffect(() => { api.get(`/reports/rotacion?dias=${dias}`).then(r => setData(r.data)); }, [dias]);
  return (
    <div className="mt-4 space-y-4">
      <Select value={String(dias)} onValueChange={(v) => setDias(parseInt(v))}>
        <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="30">Últimos 30 días</SelectItem>
          <SelectItem value="90">Últimos 90 días</SelectItem>
          <SelectItem value="180">Últimos 180 días</SelectItem>
          <SelectItem value="365">Último año</SelectItem>
        </SelectContent>
      </Select>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Código</th><th>Producto</th><th className="text-right">Vendidas</th><th className="text-right">Stock</th><th className="text-right">Rotación</th><th className="text-right">Cobertura</th></tr></thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={d.codigo_interno || `rot-${i}`}>
                <td className="font-mono text-xs">{d.codigo_interno}</td>
                <td className="font-medium">{d.nombre_comercial}</td>
                <td className="text-right tabular-nums">{d.unidades_vendidas}</td>
                <td className="text-right tabular-nums">{d.stock_actual}</td>
                <td className="text-right tabular-nums font-semibold">{d.rotacion}x</td>
                <td className="text-right tabular-nums">{d.dias_cobertura ? `${d.dias_cobertura}d` : "—"}</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">Sin datos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Bitacora() {
  const [data, setData] = useState([]);
  const [periodo, setPeriodo] = useState("mensual");
  useEffect(() => { api.get(`/reports/bitacora?periodo=${periodo}`).then(r => setData(r.data)); }, [periodo]);
  const cols = [
    { label: "Periodo", accessor: "periodo" },
    { label: "Pedidos", accessor: "pedidos" },
    { label: "Total ventas", accessor: (r) => `$${r.total.toFixed(2)}` },
    { label: "Clientes únicos", accessor: "clientes_unicos" },
  ];
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between gap-2">
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="w-[180px]" data-testid="bitacora-periodo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="diaria">Diaria</SelectItem>
            <SelectItem value="semanal">Semanal</SelectItem>
            <SelectItem value="mensual">Mensual</SelectItem>
            <SelectItem value="anual">Anual</SelectItem>
          </SelectContent>
        </Select>
        <ExportButton title={`Bitácora ${periodo}`} columns={cols} rows={data} filename={`bitacora-${periodo}`} />
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Periodo</th><th className="text-right">Pedidos</th><th className="text-right">Total</th><th className="text-right">Clientes únicos</th></tr></thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={d.periodo || `bit-${i}`}>
                <td className="font-semibold">{d.periodo}</td>
                <td className="text-right tabular-nums">{d.pedidos}</td>
                <td className="text-right tabular-nums font-semibold">${d.total.toFixed(2)}</td>
                <td className="text-right tabular-nums">{d.clientes_unicos}</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-[#8ba397]">Sin datos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function defaultDesde() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function defaultHasta() {
  return new Date().toISOString().slice(0, 10);
}

function VentasPorCliente() {
  const [desde, setDesde] = useState(defaultDesde());
  const [hasta, setHasta] = useState(defaultHasta());
  const [filtro, setFiltro] = useState("");
  const [vendedorId, setVendedorId] = useState("__all__");
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], totales: null });
  // ITER152: dialog de productos por cliente
  const [productosDialog, setProductosDialog] = useState(null); // {cliente, data}

  // ITER169: cargar lista de vendedores (roles ventas/vendedor + admin/gerencia).
  // Se usa /users/basic (accesible a cualquier usuario autenticado) para que
  // gerencia también pueda filtrar por vendedor. /users requiere admin.
  useEffect(() => {
    api.get("/users/basic")
      .then(r => {
        const users = Array.isArray(r.data) ? r.data : (r.data.items || []);
        const filtrados = users
          .filter(u => u.activo !== false)
          .filter(u => ["ventas", "vendedor", "admin", "gerencia"].includes((u.role || "").toLowerCase()));
        setVendedores(filtrados);
      })
      .catch(() => setVendedores([]));
  }, []);

  const verProductos = async (cliente) => {
    setProductosDialog({ cliente, data: null, tendencia: null, loading: true });
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      const [resp1, resp2] = await Promise.all([
        api.get(`/reports/ventas-por-cliente/${cliente.cliente_id}/productos?${params.toString()}`),
        api.get(`/reports/ventas-por-cliente/${cliente.cliente_id}/tendencia?meses=12`),
      ]);
      setProductosDialog({ cliente, data: resp1.data, tendencia: resp2.data, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setProductosDialog(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      if (vendedorId && vendedorId !== "__all__") params.set("vendedor_id", vendedorId);
      const { data: d } = await api.get(`/reports/ventas-por-cliente?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const f = (filtro || "").toLowerCase().trim();
    if (!f) return data.items;
    return (data.items || []).filter(x => (
      (x.cliente_nombre || "").toLowerCase().includes(f) ||
      (x.cliente_ruc || "").toLowerCase().includes(f) ||
      (x.vendedor_principal || "").toLowerCase().includes(f)
    ));
  }, [data.items, filtro]);

  const cols = [
    { label: "Cliente", accessor: "cliente_nombre" },
    { label: "RUC/CI", accessor: (r) => r.cliente_ruc || "—" },
    { label: "N° Facturas", accessor: "n_facturas" },
    { label: "Subtotal", accessor: (r) => `$${(r.subtotal || 0).toFixed(2)}` },
    { label: "IVA", accessor: (r) => `$${(r.iva || 0).toFixed(2)}` },
    { label: "Total", accessor: (r) => `$${(r.total || 0).toFixed(2)}` },
    { label: "Ticket prom.", accessor: (r) => `$${(r.ticket_promedio || 0).toFixed(2)}` },
    { label: "Primera compra", accessor: "primera_compra" },
    { label: "Última compra", accessor: "ultima_compra" },
    { label: "Vendedor", accessor: "vendedor_principal" },
  ];

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2 items-end p-3 rounded-md border border-[#d1dbd6] bg-white">
        <div><Label className="text-[10px] uppercase">Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="ventas-cliente-desde" className="h-8" /></div>
        <div><Label className="text-[10px] uppercase">Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="ventas-cliente-hasta" className="h-8" /></div>
        <div className="min-w-[180px]">
          <Label className="text-[10px] uppercase">Vendedor</Label>
          <Select value={vendedorId} onValueChange={setVendedorId}>
            <SelectTrigger className="h-8" data-testid="ventas-cliente-vendedor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los vendedores</SelectItem>
              {vendedores.map(v => (
                <SelectItem key={v.id} value={v.id}>
                  {`${v.nombres || ""} ${v.apellidos || ""}`.trim() || v.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading} className="bg-[#205c45] hover:bg-[#1a4a37] h-8" data-testid="ventas-cliente-aplicar"><Search size={14} className="mr-1" />Aplicar</Button>
        <div className="flex-1 min-w-[180px]"><Label className="text-[10px] uppercase">Buscar</Label><Input placeholder="Cliente, RUC, vendedor…" value={filtro} onChange={(e) => setFiltro(e.target.value)} className="h-8" /></div>
        <ExportButton title={`Ventas por cliente ${desde} → ${hasta}`} columns={cols} rows={filtered} filename={`ventas-cliente-${desde}-${hasta}`} />
      </div>
      {data.totales && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Clientes" value={data.totales.clientes} />
          <KPI label="N° Facturas" value={data.totales.n_facturas} />
          <KPI label="Subtotal" value={`$${(data.totales.subtotal || 0).toFixed(2)}`} />
          <KPI label="IVA" value={`$${(data.totales.iva || 0).toFixed(2)}`} />
          <KPI label="Total" value={`$${(data.totales.total || 0).toFixed(2)}`} highlight />
        </div>
      )}
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table">
          <thead><tr>
            <th>Cliente</th><th>RUC/CI</th>
            <th className="text-right">Facturas</th>
            <th className="text-right">Subtotal</th><th className="text-right">IVA</th><th className="text-right">Total</th>
            <th className="text-right">Ticket prom.</th>
            <th>Primera</th><th>Última</th>
            <th>Vendedor principal</th>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.cliente_id} data-testid={`ventas-cliente-row-${r.cliente_id}`}>
                <td className="font-medium">{r.cliente_nombre}</td>
                <td className="text-xs font-mono">{r.cliente_ruc || "—"}</td>
                <td className="text-right tabular-nums">{r.n_facturas}</td>
                <td className="text-right tabular-nums">${(r.subtotal || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums">${(r.iva || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${(r.total || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums">${(r.ticket_promedio || 0).toFixed(2)}</td>
                <td className="text-xs">{r.primera_compra || "—"}</td>
                <td className="text-xs">{r.ultima_compra || "—"}</td>
                <td className="text-xs">{r.vendedor_principal || "—"}</td>
                <td>
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => verProductos(r)}
                    title="Ver productos vendidos"
                    data-testid={`ver-productos-${r.cliente_id}`}
                  >
                    <ShoppingBag size={14} className="text-[#205c45]" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-[#8ba397]">Sin ventas en el rango</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ITER152: Dialog detalle de productos */}
      <Dialog open={!!productosDialog} onOpenChange={(v) => { if (!v) setProductosDialog(null); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="productos-cliente-dialog">
          <DialogHeader>
            <DialogTitle>
              <Package className="inline mr-2 mb-1" size={18} />
              Productos vendidos · {productosDialog?.cliente?.cliente_nombre}
              <span className="text-xs text-[#5c7368] ml-2">{desde} → {hasta}</span>
            </DialogTitle>
          </DialogHeader>
          {productosDialog?.loading && <div className="py-8 text-center text-[#8ba397]">Cargando…</div>}
          {productosDialog?.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPI label="Líneas" value={productosDialog.data.totales.n_lineas} />
                <KPI label="Productos distintos" value={productosDialog.data.totales.n_productos_distintos} />
                <KPI label="Cantidad total" value={productosDialog.data.totales.cantidad_total.toFixed(2)} />
                <KPI label="Subtotal" value={`$${productosDialog.data.totales.subtotal_total.toFixed(2)}`} highlight />
              </div>
              {productosDialog.tendencia && (
                <TendenciaMensual data={productosDialog.tendencia.items} />
              )}
              <Tabs2 defaultValue="resumen">
                <TabsList2>
                  <TabsTrigger2 value="resumen">Resumen por producto</TabsTrigger2>
                  <TabsTrigger2 value="detalle">Detalle por factura</TabsTrigger2>
                </TabsList2>
                <TabsContent2 value="resumen">
                  <ExportButton
                    title={`Productos a ${productosDialog.cliente.cliente_nombre} ${desde}→${hasta}`}
                    columns={[
                      { label: "Producto", accessor: "producto_nombre" },
                      { label: "Código", accessor: "codigo_interno" },
                      { label: "# Facturas", accessor: "n_facturas" },
                      { label: "Cant. total", accessor: (r) => r.cantidad_total.toFixed(2) },
                      { label: "Subtotal", accessor: (r) => `$${r.subtotal_total.toFixed(2)}` },
                    ]}
                    rows={productosDialog.data.resumen_por_producto}
                    filename={`productos-${productosDialog.cliente.cliente_nombre}-${desde}`}
                  />
                  <table className="erp-table text-xs mt-2">
                    <thead><tr>
                      <th>Producto</th><th>Código</th>
                      <th className="text-right"># Facturas</th>
                      <th className="text-right">Cant. total</th>
                      <th className="text-right">Subtotal</th>
                    </tr></thead>
                    <tbody>
                      {productosDialog.data.resumen_por_producto.map((p, i) => (
                        <tr key={i}>
                          <td className="font-medium">{p.producto_nombre}</td>
                          <td className="font-mono text-[11px]">{p.codigo_interno || "—"}</td>
                          <td className="text-right tabular-nums">{p.n_facturas}</td>
                          <td className="text-right tabular-nums">{p.cantidad_total.toFixed(2)}</td>
                          <td className="text-right tabular-nums font-semibold">${p.subtotal_total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TabsContent2>
                <TabsContent2 value="detalle">
                  <ExportButton
                    title={`Detalle productos ${productosDialog.cliente.cliente_nombre} ${desde}→${hasta}`}
                    columns={[
                      { label: "Factura", accessor: "factura_numero" },
                      { label: "Fecha", accessor: "fecha" },
                      { label: "Producto", accessor: "producto_nombre" },
                      { label: "Lote", accessor: "lote" },
                      { label: "Cantidad", accessor: "cantidad" },
                      { label: "P. Unit.", accessor: (r) => `$${r.precio_unitario.toFixed(4)}` },
                      { label: "Desc.", accessor: (r) => `$${(r.descuento || 0).toFixed(2)}` },
                      { label: "Subtotal", accessor: (r) => `$${r.subtotal.toFixed(2)}` },
                      { label: "Vendedor", accessor: "vendedor_nombre" },
                    ]}
                    rows={productosDialog.data.items}
                    filename={`detalle-${productosDialog.cliente.cliente_nombre}-${desde}`}
                  />
                  <table className="erp-table text-xs mt-2">
                    <thead><tr>
                      <th>Factura</th><th>Fecha</th><th>Producto</th><th>Lote</th>
                      <th className="text-right">Cant.</th>
                      <th className="text-right">P. Unit.</th>
                      <th className="text-right">Desc.</th>
                      <th className="text-right">Subtotal</th>
                      <th>Vendedor</th>
                    </tr></thead>
                    <tbody>
                      {productosDialog.data.items.map((it, i) => (
                        <tr key={i}>
                          <td className="font-mono text-[11px]">{it.factura_numero}</td>
                          <td>{it.fecha}</td>
                          <td className="font-medium">{it.producto_nombre}</td>
                          <td className="font-mono text-[11px]">{it.lote}</td>
                          <td className="text-right tabular-nums">{it.cantidad.toFixed(2)}</td>
                          <td className="text-right tabular-nums">${it.precio_unitario.toFixed(4)}</td>
                          <td className="text-right tabular-nums">${(it.descuento || 0).toFixed(2)}</td>
                          <td className="text-right tabular-nums font-semibold">${it.subtotal.toFixed(2)}</td>
                          <td className="text-xs">{it.vendedor_nombre || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TabsContent2>
              </Tabs2>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductosDialog(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ComisionesCobros() {
  const [desde, setDesde] = useState(defaultDesde());
  const [hasta, setHasta] = useState(defaultHasta());
  const [porcDefault, setPorcDefault] = useState(5);
  const [vendedorId, setVendedorId] = useState("all");
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], totales: null });
  const [bucketFilter, setBucketFilter] = useState("todos");
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    api.get("/users/basic").then(({ data: us }) => {
      // ITER162 (Feb 2026) — Bug fix: usa /users/basic (accesible a todos los
      // autenticados) en vez de /users (admin-only) que devolvía 403 a gerencia
      // y dejaba el Select de vendedor vacío.
      const list = (Array.isArray(us) ? us : []).filter(u =>
        u.activo !== false && (
          u.role === "ventas" || u.role === "vendedor" ||
          u.role === "admin" || u.role === "gerencia"
        )
      );
      setVendedores(list);
    }).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      if (porcDefault) params.set("porc_default", String(porcDefault));
      if (vendedorId && vendedorId !== "all") params.set("vendedor_id", vendedorId);
      const { data: d } = await api.get(`/reports/cobros-comisiones?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flatRows = useMemo(() => {
    const out = [];
    (data.items || []).forEach(v => {
      (v.items || []).forEach(it => out.push({ vendedor_nombre: v.vendedor_nombre, ...it }));
    });
    return out;
  }, [data.items]);
  const cols = [
    { label: "Vendedor", accessor: "vendedor_nombre" },
    { label: "Cobro #", accessor: "cobro_numero" },
    { label: "Fecha cobro", accessor: "fecha_cobro" },
    { label: "Método", accessor: "metodo" },
    { label: "Factura", accessor: "factura_numero" },
    { label: "Cliente", accessor: "cliente_nombre" },
    { label: "Monto cobrado", accessor: (r) => `$${(r.monto_cobrado || 0).toFixed(2)}` },
    { label: "%", accessor: (r) => `${(r.porcentaje || 0).toFixed(2)}%` },
    { label: "Origen %", accessor: "porcentaje_origen" },
    { label: "Comisión", accessor: (r) => `$${(r.valor_comision || 0).toFixed(2)}` },
  ];

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2 items-end p-3 rounded-md border border-[#d1dbd6] bg-white">
        <div><Label className="text-[10px] uppercase">Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="com-desde" className="h-8" /></div>
        <div><Label className="text-[10px] uppercase">Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="com-hasta" className="h-8" /></div>
        <div><Label className="text-[10px] uppercase">% por defecto</Label><Input type="number" step="0.1" min="0" value={porcDefault} onChange={(e) => setPorcDefault(e.target.value)} className="h-8 w-24" data-testid="com-porc-default" /></div>
        <div>
          <Label className="text-[10px] uppercase">Vendedor</Label>
          <Select value={vendedorId} onValueChange={setVendedorId}>
            <SelectTrigger className="h-8 w-56" data-testid="com-vendedor-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {vendedores.map(v => (
                <SelectItem key={v.id} value={v.id}>{`${v.nombres || ""} ${v.apellidos || ""}`.trim() || v.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading} className="bg-[#205c45] hover:bg-[#1a4a37] h-8" data-testid="com-aplicar"><Search size={14} className="mr-1" />Aplicar</Button>
        <ExportButton title={`Comisiones por cobros ${desde} → ${hasta}`} columns={cols} rows={flatRows} filename={`comisiones-${desde}-${hasta}`} />
      </div>
      {data.totales && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Vendedores" value={data.totales.vendedores} />
          <KPI label="Facturas cobradas" value={data.totales.n_facturas} />
          <KPI label="Total cobrado" value={`$${(data.totales.total_cobrado || 0).toFixed(2)}`} />
          <KPI label="Total comisión" value={`$${(data.totales.total_comision || 0).toFixed(2)}`} highlight />
        </div>
      )}
      {/* Chips de filtro por bucket global */}
      {data.totales?.buckets && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="bucket-filter-chips">
          {[
            { k: "todos", l: "Todos", icon: null, color: "bg-slate-100 text-slate-700 border-slate-300" },
            { k: "dentro_credito", l: "Dentro de crédito", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
            { k: "hasta_6m", l: "Fuera de crédito · hasta 6 meses", icon: Clock, color: "bg-amber-100 text-amber-900 border-amber-300" },
            { k: "pasado_6m", l: "Pasado 6 meses", icon: AlertCircle, color: "bg-rose-100 text-rose-800 border-rose-300" },
          ].map(b => {
            const stats = b.k === "todos"
              ? { n: data.totales.n_facturas, total: data.totales.total_cobrado }
              : data.totales.buckets[b.k] || { n: 0, total: 0 };
            const active = bucketFilter === b.k;
            const Icon = b.icon;
            return (
              <button
                key={b.k}
                type="button"
                onClick={() => setBucketFilter(b.k)}
                className={`px-3 py-1.5 rounded-md border font-medium transition-colors ${active ? "ring-2 ring-offset-1 ring-[#205c45] " + b.color : "hover:bg-[#e9f2ee] " + b.color + " opacity-70"}`}
                data-testid={`bucket-chip-${b.k}`}
              >
                {Icon && <Icon size={12} className="inline mr-1 mb-0.5" />}
                {b.l} · <b>{stats.n}</b> · <span className="tabular-nums">${(stats.total || 0).toFixed(2)}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="space-y-4">
        {(data.items || []).map(v => {
          const itemsFiltered = bucketFilter === "todos"
            ? (v.items || [])
            : (v.items || []).filter(it => it.bucket === bucketFilter);
          if (bucketFilter !== "todos" && itemsFiltered.length === 0) return null;
          const bStats = v.buckets || {};
          return (
          <div key={v.vendedor_id || "sin"} className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden" data-testid={`vendedor-group-${v.vendedor_id || "sin"}`}>
            <div className="px-3 py-2 bg-[#f3f8f5] border-b flex items-center justify-between text-sm flex-wrap gap-2">
              <div className="font-semibold text-[#15241d]">{v.vendedor_nombre}</div>
              <div className="flex gap-4 text-xs text-[#5c7368]">
                <span>{v.n_facturas_cobradas} cobros</span>
                <span>Total cobrado: <b className="text-[#15241d]">${v.total_cobrado.toFixed(2)}</b></span>
                <span>Comisión: <b className="text-[#205c45]">${v.total_comision.toFixed(2)}</b></span>
              </div>
            </div>
            {/* Mini KPI por bucket del vendedor */}
            <div className="px-3 py-2 border-b bg-white flex flex-wrap gap-3 text-[11px]">
              <span className="text-[#205c45] font-medium">
                <CheckCircle2 size={11} className="inline mr-1" />
                Dentro crédito: <b>{bStats.dentro_credito?.n || 0}</b> · <span className="tabular-nums">${(bStats.dentro_credito?.total || 0).toFixed(2)}</span>
              </span>
              <span className="text-[#a5560c] font-medium">
                <Clock size={11} className="inline mr-1" />
                Fuera crédito ≤6m: <b>{bStats.hasta_6m?.n || 0}</b> · <span className="tabular-nums">${(bStats.hasta_6m?.total || 0).toFixed(2)}</span>
              </span>
              <span className="text-[#b53a25] font-medium">
                <AlertCircle size={11} className="inline mr-1" />
                Pasado 6m: <b>{bStats.pasado_6m?.n || 0}</b> · <span className="tabular-nums">${(bStats.pasado_6m?.total || 0).toFixed(2)}</span>
              </span>
              {(bStats.sin_fecha?.n || 0) > 0 && (
                <span className="text-slate-500">Sin fecha: {bStats.sin_fecha.n}</span>
              )}
            </div>
            <table className="erp-table text-xs">
              <thead><tr>
                <th>Cobro</th><th>Fecha</th><th>Método</th><th>Factura</th><th>Cliente</th>
                <th className="text-right">Cobrado</th>
                <th className="text-center">Cumple</th>
                <th className="text-right">%</th><th className="text-right">Comisión</th>
                <th className="w-8"></th>
              </tr></thead>
              <tbody>
                {itemsFiltered.map((it, idx) => {
                  let bucketBadge = null;
                  if (it.bucket === "dentro_credito") bucketBadge = <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300"><CheckCircle2 size={9} />Sí</span>;
                  else if (it.bucket === "hasta_6m") bucketBadge = <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300"><Clock size={9} />+{Math.max(0, (it.dias_hasta_cobro || 0) - (it.dias_credito || 0))}d</span>;
                  else if (it.bucket === "pasado_6m") bucketBadge = <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-100 text-rose-800 border border-rose-300"><AlertCircle size={9} />+6m</span>;
                  else bucketBadge = <span className="text-[10px] text-slate-400">—</span>;
                  return (
                    <tr key={idx} data-testid={`cobro-row-${it.cobro_id || idx}`}>
                      <td className="font-mono">{it.cobro_numero}</td>
                      <td>{it.fecha_cobro}</td>
                      <td>{it.metodo}</td>
                      <td className="font-mono">{it.factura_numero}</td>
                      <td>
                        {it.cliente_nombre}
                        {it.reasignado && (
                          <span
                            className="ml-1 text-[10px] text-[#a5560c] font-semibold"
                            title={`Vendedor histórico (venta): ${it.vendedor_historico_nombre || '—'} · Vendedor actual: ${it.vendedor_actual_nombre || '—'}`}
                          >
                            (reasignado)
                          </span>
                        )}
                      </td>
                      <td className="text-right tabular-nums">${it.monto_cobrado.toFixed(2)}</td>
                      <td className="text-center">{bucketBadge}</td>
                      <td className="text-right tabular-nums">{it.porcentaje.toFixed(2)}%</td>
                      <td className="text-right tabular-nums font-semibold text-[#205c45]">${it.valor_comision.toFixed(2)}</td>
                      <td>
                        <button
                          onClick={() => setDetalle(it)}
                          className="p-1 rounded hover:bg-[#e9f2ee] text-[#205c45]"
                          title="Ver detalle del cobro"
                          data-testid={`detalle-cobro-${it.cobro_id || idx}`}
                        >
                          <Eye size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {itemsFiltered.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-6 text-[#8ba397]">Sin cobros en este filtro</td></tr>
                )}
              </tbody>
            </table>
          </div>
          );
        })}
        {(data.items || []).length === 0 && (
          <div className="text-center py-10 text-[#8ba397] border border-dashed rounded">Sin cobros en el rango</div>
        )}
      </div>

      {/* Modal detalle de cobro */}
      <Dialog open={!!detalle} onOpenChange={(v) => { if (!v) setDetalle(null); }}>
        <DialogContent className="max-w-lg" data-testid="detalle-cobro-dialog">
          <DialogHeader>
            <DialogTitle>Detalle del cobro {detalle?.cobro_numero}</DialogTitle>
          </DialogHeader>
          {detalle && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Factura</div><div className="font-mono">{detalle.factura_numero || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha factura</div><div>{detalle.fecha_factura || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha cobro</div><div>{detalle.fecha_cobro || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Método</div><div className="capitalize">{detalle.metodo || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Monto factura</div><div className="tabular-nums">${(detalle.monto_factura || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Valor pagado (este cobro)</div><div className="tabular-nums font-semibold text-[#205c45]">${(detalle.monto_cobrado || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Saldo actual</div><div className="tabular-nums">${(detalle.saldo_actual || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Días de crédito</div><div>{detalle.dias_credito || 0} días</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Días transcurridos hasta el cobro</div>
                  <div className={detalle.bucket === "pasado_6m" ? "text-[#b53a25] font-semibold" : detalle.bucket === "hasta_6m" ? "text-[#a5560c] font-semibold" : "text-[#205c45] font-semibold"}>
                    {detalle.dias_hasta_cobro != null ? `${detalle.dias_hasta_cobro} días` : "—"}
                    {detalle.dias_hasta_cobro != null && detalle.dias_credito ? (
                      detalle.dias_hasta_cobro <= detalle.dias_credito
                        ? <span className="ml-1 text-[10px]">(cumplió crédito)</span>
                        : <span className="ml-1 text-[10px]">(+{detalle.dias_hasta_cobro - detalle.dias_credito}d de retraso)</span>
                    ) : null}
                  </div>
                </div>
                <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Cliente</div><div>{detalle.cliente_nombre || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Vendedor actual</div><div>{detalle.vendedor_actual_nombre || "—"}</div></div>
                {detalle.reasignado && (
                  <div><div className="text-[10px] uppercase tracking-wider text-[#a5560c]">Vendedor al momento de la venta</div><div>{detalle.vendedor_historico_nombre || "—"}</div></div>
                )}
                <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observaciones</div><div className="text-[13px]">{detalle.observaciones || <span className="text-[#8ba397]">Sin observaciones</span>}</div></div>
                <div className="col-span-2 pt-2 border-t flex justify-between">
                  <span className="text-xs text-[#5c7368]">Comisión aplicada ({detalle.porcentaje?.toFixed(2)}% · {detalle.porcentaje_origen})</span>
                  <span className="font-bold text-[#205c45] tabular-nums">${(detalle.valor_comision || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalle(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, highlight }) {
  return (
    <div className={`rounded-md border ${highlight ? "border-[#205c45] bg-[#f3f8f5]" : "border-[#d1dbd6] bg-white"} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">{label}</div>
      <div className={`text-lg font-semibold ${highlight ? "text-[#205c45]" : "text-[#15241d]"} tabular-nums mt-1`}>{value}</div>
    </div>
  );
}



function TendenciaMensual({ data }) {
  if (!data || data.length === 0) return null;
  const W = 720, H = 160, PAD_L = 50, PAD_R = 16, PAD_T = 16, PAD_B = 32;
  const max = Math.max(1, ...data.map(d => d.total));
  const min = 0;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const step = data.length > 1 ? innerW / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = PAD_L + i * step;
    const y = PAD_T + innerH - ((d.total - min) / (max - min || 1)) * innerH;
    return { x, y, d };
  });
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L${points[points.length - 1].x.toFixed(1)},${(H - PAD_B).toFixed(1)} L${points[0].x.toFixed(1)},${(H - PAD_B).toFixed(1)} Z`;
  // ticks Y
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => ({ v: min + t * (max - min), y: PAD_T + innerH - t * innerH }));
  const fmtUSD = (v) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
  const totalPeriodo = data.reduce((a, b) => a + b.total, 0);
  const promedio = totalPeriodo / data.length;
  const ultimoIdx = data.length - 1;
  const ultimo = data[ultimoIdx]?.total || 0;
  const previo = data[ultimoIdx - 1]?.total || 0;
  const variacion = previo > 0 ? ((ultimo - previo) / previo) * 100 : 0;
  return (
    <div className="border border-[#d1dbd6] rounded p-3 bg-white" data-testid="tendencia-mensual">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Tendencia mensual (últimos {data.length} meses)</div>
        <div className="flex gap-3 text-[11px] text-[#5c7368]">
          <span>Total: <b className="text-[#15241d]">${totalPeriodo.toFixed(2)}</b></span>
          <span>Promedio: <b className="text-[#15241d]">${promedio.toFixed(2)}</b></span>
          <span className={variacion >= 0 ? "text-[#205c45]" : "text-[#b53a25]"}>
            Último mes: <b>{variacion >= 0 ? "▲" : "▼"} {Math.abs(variacion).toFixed(1)}%</b>
          </span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={t.y} y2={t.y} stroke="#eee" strokeWidth="1" />
            <text x={PAD_L - 6} y={t.y + 3} textAnchor="end" fontSize="10" fill="#8ba397">{fmtUSD(t.v)}</text>
          </g>
        ))}
        <path d={areaD} fill="#205c45" opacity="0.12" />
        <path d={pathD} fill="none" stroke="#205c45" strokeWidth="2" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill="#205c45" />
            <text x={p.x} y={H - PAD_B + 14} textAnchor="middle" fontSize="9" fill="#5c7368">
              {p.d.periodo.slice(2).replace("-", "/")}
            </text>
            <title>{`${p.d.periodo}: $${p.d.total.toFixed(2)} (${p.d.n_facturas} fact.)`}</title>
          </g>
        ))}
      </svg>
    </div>
  );
}
